import { Calendar, Search, X } from "lucide-react";
import "./TransactionsFilters.css";
import { useState } from "react";
import { PaginationFilters } from "../../Types/Pagination.type";
import DatePicker from "react-datepicker";
import Select, { SingleValue } from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme } from "../../contexts/ThemeContext";
import { dateToDateString, getTodayDate } from "../../utils/date.utils";

interface PaymentStatusOption {
	value: string;
	label: string;
}

interface PaymentMethodOption {
	value: string;
	label: string;
}

interface TransactionsFiltersProps {
	onSearch: (filters: PaginationFilters) => void;
	isLoading: boolean;
}

function TransactionsFilters({ onSearch, isLoading }: TransactionsFiltersProps) {
	
	const [orderId, setOrderId] = useState<string>('');
	const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOption | null>(null);
	const [paymentStatus, setPaymentStatus] = useState<PaymentStatusOption | null>(null);
	const [startDate, setStartDate] = useState<Date>(getTodayDate());
	const [endDate, setEndDate] = useState<Date>(getTodayDate());
	const { theme } = useTheme();

	const handleSearch = () => {
		if (!startDate || !endDate) return;
		onSearch(getFilters());
	};

	const getFilters = (): PaginationFilters => {
		return {
			start_date: dateToDateString(startDate),
			end_date: dateToDateString(endDate),
			...(orderId && { order_id: orderId }),
			...(paymentMethod && paymentMethod.value !== 'all' && { payment_method: paymentMethod.value }),
			...(paymentStatus && paymentStatus.value !== 'all' && { payment_status: paymentStatus.value }),
		}
	}

	const handleStartDateChange = (date: Date | null) => {
		if (date) setStartDate(date);
	};

	const handleEndDateChange = (date: Date | null) => {
		if (date) setEndDate(date);
	};

	const paymentMethodOptions: PaymentMethodOption[] = [
		{ value: 'all', label: 'Todos' },
		{ value: 'card', label: 'Tarjeta' },
		{ value: 'qr', label: 'QR' },
	];

	const paymentStatusOptions: PaymentStatusOption[] = [
		{ value: 'all', label: 'Todos' },
		{ value: 'paid', label: 'Pagado' },
		{ value: 'pending', label: 'Pendiente' },
		{ value: 'error', label: 'Error' },
		{ value: 'cancelled', label: 'Cancelado' },
	];

	const handlePaymentStatusChange = (newValue: SingleValue<PaymentStatusOption>) => {
    setPaymentStatus(newValue);
  };

	const handlePaymentMethodChange = (newValue: SingleValue<PaymentMethodOption>) => {
    setPaymentMethod(newValue);
  };

  const handleClearInput = () => {
    setOrderId('');
  };
	
	return (
		<div className="container">
			<div className="left-aligned-filters">
				{/* Search by Id */}
				<div className="id-filter-container">
					<label htmlFor="id">Buscar por Identificador</label>
					<div className="inputWrapper">
						{orderId ? (<X className="icon clear-icon" onClick={handleClearInput} />) : <Search className="icon search-icon" />}
						<input
							type="text"
							value={orderId}
							onChange={(e) => setOrderId(e.target.value)}
							className="inputSearch"
							name="id"
							placeholder="Identificador"
						/>
					</div>
				</div>
			</div>

			<div className="right-aligned-filters">
				{/* Filter by Payment Method */}
				<div className="payment-method-filter-container">
					<label htmlFor="paymentMethod">Método de pago</label>
					<div className="inputWrapper">
						<Select<PaymentMethodOption, false>
							placeholder="Todos"
							options={paymentMethodOptions}
							isSearchable={false}
							defaultValue={paymentMethod}
							onChange={handlePaymentMethodChange}
							classNamePrefix="select"
							styles={{
								control: (base) => ({
									...base,
									width: '110px',
									fontSize: '13px',
								}),
							}}
						/>
					</div>
				</div>

        {/* Filter by Payment Status */}
        <div className="payment-status-filter-container">
          <label htmlFor="paymentStatus">Estado de pago</label>
          <div className="inputWrapper">
            <Select<PaymentStatusOption, false>
							placeholder="Todos"
							classNamePrefix="select"
							options={paymentStatusOptions}
							isSearchable={false}
              defaultValue={paymentStatus}
              onChange={handlePaymentStatusChange}
							styles={{
								control: (base) => ({
									...base,
									width: '110px',
									fontSize: '13px',
								}),
							}}
            />
          </div>
        </div>

				{/* Filter by Date From */}
				<div className="date-filter-container">
					<label htmlFor="fromDate">Desde</label>
					<div className="inputWrapper">
						<Calendar className="icon" />
						<DatePicker
							selected={startDate}
							onChange={handleStartDateChange}
							className="inputDate"
							dateFormat="dd/MM/yyyy"
							placeholderText="dd/mm/aaaa"
							wrapperClassName="date-picker-wrapper"
							calendarClassName={`date-picker-calendar ${theme}`}
						/>
					</div>
				</div>

				{/* Filter by Date To */}
				<div className="date-filter-container">
					<label htmlFor="toDate">Hasta</label>
					<div className="inputWrapper">
						<Calendar className="icon" />
						<DatePicker
							selected={endDate}
							onChange={handleEndDateChange}
							className="inputDate"
							dateFormat="dd/MM/yyyy"
							placeholderText="dd/mm/aaaa"
							wrapperClassName="date-picker-wrapper"
							calendarClassName={`date-picker-calendar ${theme}`}
						/>
					</div>
				</div>

				{/* Search Button */}
				<div>
					<button 
						className="searchButton" 
						onClick={handleSearch}
						disabled={isLoading || !startDate || !endDate}
					>
						{isLoading ? 'Buscando...' : 'Buscar'}
					</button>
				</div>
			</div>
		</div>
	);
}

export default TransactionsFilters;
